import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Email and password required.");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Authenticated.");
      const dest = location.state?.from?.pathname || "/admin";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-axiom-bg relative overflow-hidden px-4 py-12"
    >
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="absolute inset-0 bg-radial-fade pointer-events-none" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="flex items-center gap-2 mb-10 justify-center"
          data-testid="admin-login-brand"
        >
          <span className="signal-dot animate-pulse-signal" />
          <span className="font-display text-xl font-bold tracking-tight text-axiom-text">
            AXIOM-AI
          </span>
        </Link>

        <div className="axiom-card p-8">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={16} className="text-axiom-accent" />
            <span className="num-label">SECURE TRANSMISSION</span>
          </div>
          <h1 className="font-display text-3xl text-axiom-text font-bold tracking-tight">
            Admin Access
          </h1>
          <p className="mt-2 text-sm text-axiom-muted">
            Restricted area. Authorized operators only.
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-8 space-y-4"
            data-testid="admin-login-form"
          >
            <label className="block">
              <span className="num-label">EMAIL</span>
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                className="mt-1.5 w-full bg-axiom-bg border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none transition-colors"
                placeholder="admin@axiom-ai.com"
              />
            </label>

            <label className="block">
              <span className="num-label">PASSWORD</span>
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1.5 w-full bg-axiom-bg border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none transition-colors"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <p
                data-testid="admin-login-error"
                className="text-sm text-red-400 font-mono"
              >
                ! {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="btn-axiom w-full justify-center disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Authenticating...
                </>
              ) : (
                <>
                  Sign in <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-xs text-axiom-dim font-mono flex items-center justify-between">
            <span>AXM.AUTH / JWT</span>
            <Link to="/" className="hover:text-axiom-text transition-colors">
              ← Back to site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
